import { useCallback, useEffect, useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { AuthLayout } from "~/components/auth-layout";
import { Field, fieldMessageId } from "~/components/field";
import { OtpInput } from "~/components/otp-input";
import { PhoneInput } from "~/components/phone-input";
import { Button } from "~/components/ui/button";
import { PasswordInput } from "~/components/ui/password-input";
import { api, APIError } from "~/lib/api";
import { useCompleteSignIn } from "~/lib/auth";
import { formatE164, isValidNational, toE164 } from "~/lib/phone";
import { waitForSmsCode } from "~/lib/web-otp";
import type { ISession } from "~/lib/proto";

export const Route = createFileRoute("/_public/login")({
  component: VerifyPhone,
});

type Method = "code" | "password";
type Challenge = { phone: string; resendAt: number; expiresAt: number };

const OFFLINE = "No connection. Check your internet and try again.";
const RATE_LIMITED = "Too many attempts. Wait a minute, then try again.";
const INVALID_NUMBER = "Enter your mobile number, for example 712 345 678.";

async function requestChallenge(phone: string): Promise<Challenge> {
  const res = await api.otpRequest(phone);
  const now = Date.now();
  return {
    phone,
    resendAt: now + (res.retryAfterSeconds ?? 0) * 1000,
    expiresAt: now + (res.ttlSeconds ?? 0) * 1000,
  };
}

function requestErrorMessage(e: unknown): string {
  if (!(e instanceof APIError)) return OFFLINE;
  if (e.code === "invalid_phone") return "That does not look like a Kenyan mobile number. Check it and try again.";
  if (e.code === "otp_limit") return e.message;
  if (e.status === 429) return RATE_LIMITED;
  return "We could not send the code. Try again.";
}

function verifyErrorMessage(e: unknown): string {
  if (!(e instanceof APIError)) return OFFLINE;
  if (e.code === "locked") return "Too many wrong tries. Request a new code.";
  if (e.code === "invalid_code") return "That code is not right. Check the SMS and try again.";
  if (e.status === 429) return RATE_LIMITED;
  return "We could not verify the code. Try again.";
}

function passwordErrorMessage(e: unknown): string {
  if (!(e instanceof APIError)) return OFFLINE;
  if (e.status === 429) return RATE_LIMITED;
  return "That number and password do not match. Check both, or use a code instead.";
}

function useSecondsUntil(deadline: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    setNow(Date.now());
    const timer = window.setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= deadline) window.clearInterval(timer);
    }, 500);
    return () => window.clearInterval(timer);
  }, [deadline]);
  return Math.max(0, Math.ceil((deadline - now) / 1000));
}

function clock(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function VerifyPhone() {
  const completeSignIn = useCompleteSignIn();
  const [method, setMethod] = useState<Method>("code");
  const [digits, setDigits] = useState("");
  const [challenge, setChallenge] = useState<Challenge | null>(null);

  if (method === "password") {
    return (
      <PasswordStep
        digits={digits}
        onDigits={setDigits}
        onUseCode={() => setMethod("code")}
        onSignedIn={completeSignIn}
      />
    );
  }
  if (challenge) {
    return (
      <CodeStep
        challenge={challenge}
        onChallenge={setChallenge}
        onBack={() => setChallenge(null)}
        onVerified={completeSignIn}
      />
    );
  }
  return (
    <PhoneStep
      digits={digits}
      onDigits={setDigits}
      onSent={setChallenge}
      onUsePassword={() => setMethod("password")}
    />
  );
}

function MethodSwitch({ children, onClick }: { children: string; onClick: () => void }) {
  return (
    <Button type="button" variant="ghost" className="text-muted-foreground h-11 px-4 text-[15px]" onClick={onClick}>
      {children}
    </Button>
  );
}

function PhoneStep({
  digits,
  onDigits,
  onSent,
  onUsePassword,
}: {
  digits: string;
  onDigits: (digits: string) => void;
  onSent: (challenge: Challenge) => void;
  onUsePassword: () => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!isValidNational(digits)) {
      setError(INVALID_NUMBER);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSent(await requestChallenge(toE164(digits)));
    } catch (err) {
      setError(requestErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Verify your phone number"
      description="We'll text you a 6-digit code. It signs you in, or sets up your account if you're new."
      footer={<MethodSwitch onClick={onUsePassword}>Use a password instead</MethodSwitch>}
    >
      <form className="space-y-6" onSubmit={submit} noValidate>
        <Field id="phone" label="Mobile number" error={error}>
          <PhoneInput
            id="phone"
            name="phone"
            autoFocus
            value={digits}
            invalid={!!error}
            aria-describedby={error ? fieldMessageId("phone") : undefined}
            onChange={(next) => {
              onDigits(next);
              setError(null);
            }}
          />
        </Field>
        <Button type="submit" size="touch" className="w-full" loading={busy}>
          Send code
        </Button>
      </form>
    </AuthLayout>
  );
}

function CodeStep({
  challenge,
  onChallenge,
  onBack,
  onVerified,
}: {
  challenge: Challenge;
  onChallenge: (challenge: Challenge) => void;
  onBack: () => void;
  onVerified: (session: ISession) => Promise<void>;
}) {
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const wait = useSecondsUntil(challenge.resendAt);

  const verify = useCallback(
    async (value: string) => {
      if (Date.now() > challenge.expiresAt) {
        setCode("");
        setError("That code has expired. Request a new one.");
        return;
      }
      setVerifying(true);
      setError(null);
      try {
        await onVerified(await api.otpVerify(challenge.phone, value));
      } catch (err) {
        setCode("");
        setError(verifyErrorMessage(err));
        setFailures((n) => n + 1);
        setVerifying(false);
      }
    },
    [challenge, onVerified],
  );

  useEffect(() => {
    const listening = new AbortController();
    void waitForSmsCode(listening.signal).then((smsCode) => {
      if (!smsCode) return;
      setCode(smsCode);
      void verify(smsCode);
    });
    return () => listening.abort();
  }, [verify]);

  async function resend() {
    setResending(true);
    setError(null);
    try {
      onChallenge(await requestChallenge(challenge.phone));
      setCode("");
    } catch (err) {
      setError(requestErrorMessage(err));
    } finally {
      setResending(false);
    }
  }

  return (
    <AuthLayout
      title="Enter the code"
      description={
        <>
          We sent a 6-digit code by SMS to{" "}
          <span className="text-foreground font-medium whitespace-nowrap">{formatE164(challenge.phone)}</span>.
        </>
      }
      back={
        <Button type="button" variant="ghost" className="-ml-3 h-11 px-3 text-[15px]" onClick={onBack}>
          <ChevronLeft className="size-5" />
          Change number
        </Button>
      }
    >
      <div className="space-y-4">
        <OtpInput
          key={failures}
          id="code"
          autoFocus
          aria-label="6-digit verification code"
          aria-describedby={error ? fieldMessageId("code") : undefined}
          value={code}
          invalid={!!error && !code}
          pending={verifying}
          onChange={(next) => {
            setCode(next);
            if (next) setError(null);
          }}
          onComplete={(value) => void verify(value)}
        />
        {error && (
          <p id={fieldMessageId("code")} role="alert" className="text-destructive type-footnote text-center">
            {error}
          </p>
        )}
        <div className="text-muted-foreground type-body flex min-h-11 items-center justify-center" aria-live="polite">
          {verifying ? (
            "Verifying…"
          ) : wait > 0 ? (
            <span className="tabular">Resend code in {clock(wait)}</span>
          ) : (
            <Button type="button" variant="ghost" className="h-11 px-4 text-[15px]" loading={resending} onClick={() => void resend()}>
              Resend code
            </Button>
          )}
        </div>
      </div>
    </AuthLayout>
  );
}

type PasswordErrors = { phone?: string; password?: string; form?: string };

function PasswordStep({
  digits,
  onDigits,
  onUseCode,
  onSignedIn,
}: {
  digits: string;
  onDigits: (digits: string) => void;
  onUseCode: () => void;
  onSignedIn: (session: ISession) => Promise<void>;
}) {
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<PasswordErrors>({});
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const found: PasswordErrors = {};
    if (!isValidNational(digits)) found.phone = INVALID_NUMBER;
    if (!password) found.password = "Enter your password.";
    setErrors(found);
    if (found.phone || found.password) return;

    setBusy(true);
    try {
      await onSignedIn(await api.passwordLogin(toE164(digits), password));
    } catch (err) {
      setErrors({ form: passwordErrorMessage(err) });
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in with your password"
      description="For accounts that have set a password. If you have not, use a code instead."
      footer={<MethodSwitch onClick={onUseCode}>Use a code instead</MethodSwitch>}
    >
      <form className="space-y-5" onSubmit={submit} noValidate>
        <Field id="phone" label="Mobile number" error={errors.phone}>
          <PhoneInput
            id="phone"
            name="phone"
            autoFocus
            value={digits}
            invalid={!!errors.phone}
            aria-describedby={errors.phone ? fieldMessageId("phone") : undefined}
            onChange={(next) => {
              onDigits(next);
              setErrors((prev) => ({ ...prev, phone: undefined, form: undefined }));
            }}
          />
        </Field>
        <Field id="password" label="Password" error={errors.password}>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            className="h-12 rounded-lg pl-3.5 text-[16px]"
            value={password}
            aria-invalid={!!errors.password || undefined}
            aria-describedby={errors.password ? fieldMessageId("password") : undefined}
            onChange={(e) => {
              setPassword(e.target.value);
              setErrors((prev) => ({ ...prev, password: undefined, form: undefined }));
            }}
          />
        </Field>
        {errors.form && (
          <p role="alert" className="text-destructive type-footnote">
            {errors.form}
          </p>
        )}
        <Button type="submit" size="touch" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>
    </AuthLayout>
  );
}
